'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Header from '@/components/common/Header';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="mb-12">
          <Link 
            href="/homepage" 
            className="inline-flex items-center space-x-2 text-accent hover:text-accent/80 transition-colors duration-300 mb-6"
          >
            <Icon name="ArrowLeftIcon" size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Terms and Conditions
          </h1>
          <p className="text-foreground/70">
            Last updated: February 11, 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              1. Introduction
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              Welcome to CIT SMART SOLUTIONS LTD ("Company", "we", "our", "us"). These Terms and Conditions ("Terms") govern your use of our website and services. By accessing or using our services, you agree to be bound by these Terms.
            </p>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="text-sm text-foreground/70">
                <strong>Company Details:</strong><br />
                CIT SMART SOLUTIONS LTD<br />
                First Floor Office, 3 Hornton Place, London, W8 4LZ, UNITED KINGDOM<br />
                Registered in UNITED KINGDOM, Number 17015313
              </p>
            </div>
          </section>

          {/* Services */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              2. Services
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              CIT SMART SOLUTIONS LTD provides AI automation consulting, digital transformation services, and marketing solutions. Our services include but are not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/80">
              <li>AI automation strategy and implementation</li>
              <li>Business process optimization</li>
              <li>Digital marketing and growth solutions</li>
              <li>Technology consulting and advisory services</li>
              <li>Custom software and automation development</li>
            </ul>
          </section>

          {/* User Obligations */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              3. User Obligations
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              When using our services, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/80">
              <li>Provide accurate and complete information</li>
              <li>Maintain the confidentiality of your account credentials</li>
              <li>Use our services in compliance with all applicable laws and regulations</li>
              <li>Not engage in any activity that disrupts or interferes with our services</li>
              <li>Not use our services for any unlawful or fraudulent purposes</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              4. Intellectual Property Rights
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              All content, materials, and intellectual property on our website and in our services, including but not limited to text, graphics, logos, images, software, and methodologies, are the property of CIT SMART SOLUTIONS LTD or our licensors and are protected by copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              5. Payment Terms
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              For paid services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/80">
              <li>Payment terms will be specified in individual service agreements</li>
              <li>All fees are exclusive of applicable taxes unless otherwise stated</li>
              <li>Late payments may incur additional charges as specified in your agreement</li>
              <li>We reserve the right to suspend services for non-payment</li>
            </ul>
          </section>

          {/* Confidentiality */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              6. Confidentiality
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              We respect the confidentiality of information shared by our clients. Both parties agree to maintain confidentiality of proprietary information disclosed during the course of our engagement, subject to the terms of any separate non-disclosure agreement.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              7. Limitation of Liability
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground/80">
              <li>CIT SMART SOLUTIONS LTD shall not be liable for any indirect, incidental, special, or consequential damages</li>
              <li>Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim</li>
              <li>We do not warrant that our services will be uninterrupted or error-free</li>
              <li>We are not responsible for delays or failures due to circumstances beyond our reasonable control</li>
            </ul>
          </section>

          {/* Warranties */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              8. Warranties and Disclaimers
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              While we strive to provide high-quality services, our services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee specific results from our services, as outcomes depend on various factors including client implementation and market conditions.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              9. Termination
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              Either party may terminate services in accordance with the terms specified in individual service agreements. Upon termination, you must cease all use of our services and materials. Provisions regarding confidentiality, intellectual property, and limitation of liability shall survive termination.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              10. Governing Law and Jurisdiction
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          {/* Data Protection */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              11. Data Protection
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              We are committed to protecting your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. For details on how we collect, use, and protect your data, please refer to our Privacy Policy.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              12. Changes to Terms
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website with a new "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">
              13. Contact Information
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <div className="bg-muted p-6 rounded-lg space-y-2">
              <p className="text-foreground/80">
                <strong>CIT SMART SOLUTIONS LTD</strong>
              </p>
              <p className="text-foreground/70">
                First Floor Office, 3 Hornton Place<br />
                London, W8 4LZ<br />
                UNITED KINGDOM
              </p>
              <p className="text-foreground/70">
                Email: <a href="mailto:info@consultintech.com" className="text-accent hover:underline">info@consultintech.com</a>
              </p>
              <p className="text-foreground/70">
                Company Registration Number: 17015313
              </p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="border-t border-border pt-8 mt-12">
            <p className="text-foreground/70 text-sm">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-authority text-authority-foreground mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-authority-foreground/80">
              © 2026 CIT SMART SOLUTIONS LTD. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link href="/privacy" className="text-authority-foreground/80 hover:text-accent transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-authority-foreground/80 hover:text-accent transition-colors duration-300">
                Terms of Service
              </Link>
              <Link href="/contact" className="text-authority-foreground/80 hover:text-accent transition-colors duration-300">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;